/**
 * Base AJAX service for handling all WordPress AJAX requests
 */
export class BaseAjaxService {
  constructor() {
    this.ajaxUrl = ajax_object.ajax_url;
    this.nonce = ajax_object.nonce;
    this.postId = ajax_object.post_id;
  }

  /**
   * Generic AJAX request handler
   * @param {string} action WordPress AJAX action
   * @param {Object} data Additional data to send
   * @param {boolean} useFormData Whether to use FormData or URLSearchParams
   * @returns {Promise<Object>} Response data
   */
  async request(action, data = {}, useFormData = false) {
    const requestData = {
      action,
      post_id: this.postId,
      ...data
    };

    const body = useFormData 
      ? this.createFormData(requestData)
      : this.createUrlParams(requestData);

    const headers = useFormData 
      ? {} // Let browser set Content-Type for FormData
      : { 'Content-Type': 'application/x-www-form-urlencoded' };

    try {
      const response = await fetch(this.ajaxUrl, {
        method: 'POST',
        headers,
        body,
        credentials: 'same-origin'
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.data || 'Request failed');
      }

      return result.data;
    } catch (error) {
      throw error;
    }
  }

  createFormData(data) {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
    });
    return formData;
  }

  createUrlParams(data) {
    const params = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      params.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
    });
    return params.toString();
  }

  // Specific methods for common actions
  async getSelectedItems(type) {
    const action = type === 'item' ? 'get_selected_items' : `get_selected_${type}`;
    return this.request(action);
  }

  async saveItemSelection(item, type) {
    const action = type === 'item' ? 'save_item_selection' : `save_${type}_selection`;
    return this.request(action, { item: JSON.stringify(item) }, true);
  }

  async clearSelectedItems(mainCatSlug, type) {
    const action = type === 'item' ? 'clear_selected_items' : `clear_selected_${type}`;
    return this.request(action, { main_cat_slug: mainCatSlug });
  }

  async confirmSelection(type, timestamp) {
    const action = type === 'item' ? 'confirm_interior' : `confirm_${type}`;
    return this.request(action, { timestamp });
  }

  async resetConfirmation(type) {
    const action = type === 'item' ? 'reset_interior_confirmation' : `reset_${type}_confirmation`;
    return this.request(action);
  }

  async updateItemPrices(items) {
    return this.request('update_item_prices', { items: JSON.stringify(items) });
  }

  async saveSelectedPlan(plan) {
    return this.request('save_selected_plan', { plan });
  }

  async saveExtraOption(title, price, isSelected) {
    return this.request('save_extra_option', { title, price, is_selected: isSelected });
  }

  async getSelectedExtras() {
    return this.request('get_selected_extras');
  }
}